import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import UtilService from "../../services/UtilService";

/**
 * Dialog showing user detail and puzzle (cross, grid, or jigsaw) detail for a play row.
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {object} rowData - Row with cloudUser and cross, grid, or jigsaw/story fields
 * @param {"cross" | "grid" | "jigsaw"} puzzleKey - Which puzzle object to show
 */
export default function PlayRowDetailDialog({ open, onClose, rowData, puzzleKey = "cross" }) {
    const user = rowData?.cloudUser ?? null;
    const puzzle = puzzleKey === "jigsaw" ? rowData?.story ?? null : rowData?.[puzzleKey] ?? null;
    const jigsaw = puzzleKey === "jigsaw" ? rowData?.jigsaw ?? null : null;
    const storyJigsaw = puzzleKey === "jigsaw" ? rowData?.storyJigsaw ?? null : null;
    const details = readPlayDetails(rowData?.details);
    const revenueCatAppUserId = details?.revenueCatAppUserId
        ? String(details.revenueCatAppUserId).trim()
        : "";
    const revenueCatOriginalAppUserId = details?.revenueCatOriginalAppUserId
        ? String(details.revenueCatOriginalAppUserId).trim()
        : "";
    const puzzleLabel =
        puzzleKey === "cross" ? "Crossword" : puzzleKey === "jigsaw" ? "Jigsaw" : "Puzzle";
    const puzzleTitle =
        puzzleKey === "jigsaw"
            ? rowData?.puzzleTitle ?? null
            : puzzle?.title ?? null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Play details</Typography>
                    <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* User */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            User
                        </Typography>
                        <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {user ? (
                                <>
                                    <Typography variant="body2">
                                        <strong>Email:</strong> {user.email ?? "—"}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Name:</strong> {[user.firstName, user.lastName].filter(Boolean).join(" ") || "—"}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Role:</strong> {user.userRole ?? "—"}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Pay plan:</strong> {UtilService.resolveWebPayPlan(user) || "—"}
                                    </Typography>
                                    {user.formattedDate && (
                                        <Typography variant="body2">
                                            <strong>Created:</strong> {user.formattedDate}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No user data
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {(revenueCatAppUserId || revenueCatOriginalAppUserId) ? (
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Details
                            </Typography>
                            <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {revenueCatAppUserId ? (
                                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                        <strong>RevenueCat app user:</strong> {revenueCatAppUserId}
                                    </Typography>
                                ) : null}
                                {revenueCatOriginalAppUserId
                                    && revenueCatOriginalAppUserId !== revenueCatAppUserId ? (
                                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                                        <strong>RevenueCat original:</strong> {revenueCatOriginalAppUserId}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    ) : null}

                    {/* Puzzle (cross or grid) */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {puzzleLabel}
                        </Typography>
                        <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {puzzle || puzzleTitle ? (
                                <>
                                    <Typography variant="body2">
                                        <strong>Title:</strong> {puzzleTitle ?? puzzle?.title ?? "—"}
                                    </Typography>
                                    {puzzleKey === "jigsaw" && rowData?.status && (
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {rowData.status}
                                        </Typography>
                                    )}
                                    {rowData?.startedAt && (
                                        <Typography variant="body2">
                                            <strong>Started:</strong> {rowData.startedAt}
                                        </Typography>
                                    )}
                                    {rowData?.finishedAt && (
                                        <Typography variant="body2">
                                            <strong>Finished:</strong> {rowData.finishedAt}
                                        </Typography>
                                    )}
                                    {puzzleKey !== "jigsaw" && (
                                        <Typography variant="body2">
                                            <strong>State:</strong> {puzzle?.state ?? "—"}
                                        </Typography>
                                    )}
                                    {storyJigsaw?.state && (
                                        <Typography variant="body2">
                                            <strong>Bundle state:</strong> {storyJigsaw.state}
                                        </Typography>
                                    )}
                                    {(puzzle?.template?.name || jigsaw?.templateName) && (
                                        <Typography variant="body2">
                                            <strong>Template:</strong>{" "}
                                            {jigsaw?.templateName ?? puzzle?.template?.name}
                                        </Typography>
                                    )}
                                    {puzzle?.genre?.name && (
                                        <Typography variant="body2">
                                            <strong>Genre:</strong> {puzzle.genre.name}
                                        </Typography>
                                    )}
                                    {(storyJigsaw?.free != null || puzzle?.free != null || jigsaw?.free != null) && (
                                        <Typography variant="body2">
                                            <strong>Free:</strong>{" "}
                                            {(storyJigsaw?.free ?? puzzle?.free ?? jigsaw?.free) ? "Yes" : "No"}
                                        </Typography>
                                    )}
                                    {(jigsaw?.difficultyLevel || puzzle?.difficultyLevel) && (
                                        <Typography variant="body2">
                                            <strong>Difficulty:</strong>{" "}
                                            {jigsaw?.difficultyLevel ?? puzzle?.difficultyLevel}
                                        </Typography>
                                    )}
                                    {jigsaw?.tileSize != null && (
                                        <Typography variant="body2">
                                            <strong>Tile size:</strong> {jigsaw.tileSize}
                                        </Typography>
                                    )}
                                    {puzzle?.type && (
                                        <Typography variant="body2">
                                            <strong>Type:</strong> {puzzle.type}
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No puzzle data
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function readPlayDetails(raw) {
    if (!raw) {
        return null;
    }
    if (typeof raw === "object" && !Array.isArray(raw)) {
        return raw;
    }
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}
